/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import { Button, Callout, CheckboxNew, ExternalLink, InlineMessage } from "@octopusdeploy/design-system-components";
import { ArrowUpFromSquareIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import type { LicenseStatusResource, StatsResourceCollection, TaskResource, TelemetryConfigurationResource } from "@octopusdeploy/octopus-server-client";
import { TaskName } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { OctoLink } from "@octopusdeploy/utilities";
import { cloneDeep } from "lodash";
import moment from "moment";
import { client, repository } from "~/clientInstance";
import type { Refresh } from "~/components/DataBaseComponent/DataBaseComponent";
import { OptionalFormBaseComponent } from "~/components/FormBaseComponent/FormBaseComponent";
import type { OptionalFormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import InternalLink from "~/components/Navigation/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { PagingList } from "~/components/PagingList/PagingList";
import Section from "~/components/Section";
import TaskDetails from "~/components/TaskDetails";
import { withTheme } from "~/components/Theme";
import TransitionAnimation from "~/components/TransitionAnimation/TransitionAnimation";
import { ExpandableFormSection, Summary } from "~/components/form";
import DateFormatter from "~/utils/DateFormatter";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
interface TelemetryPageState extends OptionalFormBaseComponentState<TelemetryConfigurationResource> {
    lastRunTask?: TaskResource;
    taskHistory?: StatsResourceCollection;
    licenseStatus?: LicenseStatusResource;
}
class TelemetryTasksList extends PagingList<TaskResource<{}>> {
}
const buildTaskRow = (task: TaskResource<{}>) => <TaskDetails task={task} stripTopBottomPadding={true}/>;
export class TelemetryPage extends OptionalFormBaseComponent<{}, TelemetryPageState, TelemetryConfigurationResource> {
    constructor(props: {}) {
        super(props);
        this.state = {};
    }
    async componentDidMount() {
        localStorage.setItem(TelemetryHasBeenIntroducedLocalStorageKey, "true");
        await this.doBusyTask(async () => {
            const configuration = await repository.TelemetryConfiguration.get();
            const task = await repository.Telemetry.getLatestTelemetryTask();
            const taskHistory = await repository.Tasks.list({ name: TaskName.SendTelemetry, skip: 0, take: 10 });
            const licenseStatus = await repository.Licenses.getCurrentStatus();
            this.doRefresh = await this.startRefreshLoop(() => this.refreshLatestTask(), 5000);
            this.setState({
                model: configuration,
                cleanModel: cloneDeep(configuration),
                lastRunTask: task,
                taskHistory,
                licenseStatus,
            });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    async loadTaskHistory(skip: number = 0) {
        const taskHistory = await repository.Tasks.list({ taskName: TaskName.SendTelemetry, skip: skip, take: 10 });
        this.setState({ taskHistory });
    }
    async refreshLatestTask() {
        const lastRunTask = await repository.Telemetry.getLatestTelemetryTask();
        const taskHistory = await repository.Tasks.list({ name: TaskName.SendTelemetry, skip: 0, take: 10 });
        return {
            ...this.state,
            lastRunTask,
            taskHistory,
        };
    }
    handleSaveClick = async () => {
        await this.doBusyTask(async () => {
            const result = await repository.TelemetryConfiguration.modify(this.state.model!);
            this.setState({
                model: result,
                cleanModel: cloneDeep(result),
            });
        });
    };
    sendTelemetry = async () => {
        const task = await repository.Tasks.createSendTelemetryTask();
        this.setState({ lastRunTask: task });
    };
    renderLastTelemetryTaskDetails = () => {
        const { lastRunTask } = this.state;
        if (!lastRunTask)
            return <p className={textContentNoMargin}>Telemetry task has not run</p>;
        if (lastRunTask.StartTime)
            return (<p className={textContentNoMargin}>
                    Telemetry task last ran{` `}
                    <InternalLink to={links.taskPage.generateUrl({ taskId: lastRunTask.Id })} className={taskTime}>
                        {DateFormatter.momentAgo(lastRunTask.StartTime!)}
                    </InternalLink>
                </p>);
        return <p className={textContentNoMargin}>Telemetry task is in the task queue</p>;
    };
    renderTelemetryDetails = () => {
        return (<ExpandableFormSection key="IsTelemetryReportingEnabled" errorKey="IsTelemetryReportingEnabled" title="Telemetry Reporting" summary={this.state.model!.Enabled ? Summary.default("Enabled") : Summary.summary("Disabled")} help="Send telemetry data to Octopus" isExpandedByDefault expandable>
                <div className={telemetryFormContent}>
                    {this.state.model?.IsTelemetryEnforced && this.renderUpsellMessage()}

                    <CheckboxNew value={!!this.state.model?.Enabled} onChange={(Enabled) => this.setModelState({ Enabled })} label="Send telemetry" disabled={this.state.model?.IsTelemetryEnforced}/>

                    <Button label="Download Telemetry Preview" href={client.resolveLinkTemplate("TelemetryDownload", {})} isDownload={true} external={true} importance={"secondary"} size="small"/>

                    {!this.state.model?.Enabled && <p className={textContentNoMargin}>When telemetry reporting is turned off, some data is collected locally but not sent to Octopus. You can send a one-time telemetry snapshot to Octopus.</p>}

                    {!this.state.model?.Enabled && <Button label="Send Telemetry Snapshot" disabled={!!this.state.busy} onClick={this.sendTelemetry} importance="secondary" size="small" icon={<ArrowUpFromSquareIcon size={20}/>}/>}
                </div>
            </ExpandableFormSection>);
    };
    renderUpsellMessage = () => {
        const upgradeLinkLabel = "Upgrade";
        const upgradeLink = OctoLink.Create(`plan-upgrade?serial=${this.state.licenseStatus?.SerialNumber}`);
        return <InlineMessage type="upsell" description="Upgrade to the Professional or Enterprise tier to turn off telemetry reporting." action={{ label: upgradeLinkLabel, href: upgradeLink, external: true, trackAnalytics: true }}/>;
    };
    renderTaskHistory = () => {
        return (<ExpandableFormSection key="TelemetryHistory" errorKey="TelemetryHistory" title="Telemetry History" summary={Summary.summary(this.renderLastTelemetryTaskDetails())} help={"View a history of telemetry tasks"} isExpandedByDefault={true} expandable>
                <TelemetryTasksList initialData={this.state.taskHistory} onRow={(item: TaskResource<{}>) => buildTaskRow(item)} onRowRedirectUrl={(task: TaskResource<{}>) => links.taskPage.generateUrl({ taskId: task.Id })} showPagingInNumberedStyle={true} currentPageIndex={0} onPageSelected={async (skip: number) => {
                await this.loadTaskHistory(skip);
            }}/>
            </ExpandableFormSection>);
    };
    renderNotification = () => {
        const now = moment();
        const ShowAsNewUntil = moment(this.state.model?.ShowAsNewUntil);
        return (ShowAsNewUntil.isAfter(now) && (<Callout type={"new-feature"} title={"Telemetry changes"}>
                    <p>We've changed the way Octopus Deploy collects and sends telemetry{";"} for better transparency and visibility.</p>
                    <p>This now occurs via a system task. You can enable or disable telemetry reporting, view the status, and preview your data on this page.</p>
                    <p>Telemetry reporting is enabled by default. Data will be sent from {ShowAsNewUntil.locale("en").format("dddd[,] DD MMMM YYYY[,] HH:mm:ss [GMT]ZZ")} unless you disable it before then.</p>
                </Callout>));
    };
    render() {
        return withTheme(() => (<LegacyForm model={this.state.model} cleanModel={this.state.cleanModel} onSaveClick={this.handleSaveClick}>
                {({ FormContent, createSaveAction }) => (<PageContent header={{ primaryAction: createSaveAction({}), title: "Telemetry" }} busy={this.state.busy}>
                        <FormContent expandAllOnMount={false} hideExpandAll={true}>
                            {this.state.model && (<TransitionAnimation>
                                    {this.renderNotification()}
                                    <Section>
                                        <p>Octopus collects telemetry data on user actions, feature usage and performance to improve the user experience and guide roadmap decisions.</p>
                                        <p>Rest assured, we don’t collect personal data or sensitive information, for example, source code, configuration files, variables, or personally identifiable information (PII).</p>
                                        <p>
                                            You can download a preview of your telemetry data below or read our <ExternalLink href="Telemetry">documentation</ExternalLink> to learn how we collect telemetry.
                                        </p>
                                    </Section>
                                    {this.renderTelemetryDetails()}
                                    {this.renderTaskHistory()}
                                </TransitionAnimation>)}
                        </FormContent>
                    </PageContent>)}
            </LegacyForm>));
    }
    private doRefresh: Refresh = () => Promise.resolve();
}
export const TelemetryHasBeenIntroducedLocalStorageKey = "TelemetryHasBeenIntroduced";
const telemetryFormContent = css({
    display: "grid",
    gap: space[16],
    margin: `${space[16]} 0`,
});
const textContentNoMargin = css({
    margin: 0,
});
const taskTime = css({
    marginRight: space[8],
});
